import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DOCUMENT_TYPE_BY_CODE, deepLink } from '@erp-attach/shared';
import { useApi } from '../api/ApiProvider';
import type { DocumentDetail } from '../api/types';
import { config } from '../config';
import { entityRoute, routes } from '../lib/links';
import { addRecentDocument, notifyRecentChange } from '../lib/recent';
import { formatBytes, formatDate, formatDateTime, shortUpn } from '../lib/format';
import { saveBlob } from '../lib/saveBlob';
import { useDocumentPreview } from '../hooks/useDocumentPreview';
import { Button } from './ui/Button';
import { Chip, StatusChip } from './ui/Chip';
import { ErrorBanner } from './ui/ErrorBanner';
import { Breadcrumbs } from './EntityHeader';
import { CopyButton } from './CopyButton';
import { DocumentPreview } from './DocumentPreview';
import { FilePlate } from './FilePlate';
import { RecordsActions } from './RecordsActions';

export interface DocumentViewerProps {
  document: DocumentDetail;
  /** Called after hide/unhide/remove/hold so the caller can refetch. */
  onChanged?: () => void;
}

const KIND_LABEL = { customer: 'Customer', site: 'Job', supplier: 'Supplier' } as const;

export function DocumentViewer({ document: doc, onChanged }: DocumentViewerProps) {
  const api = useApi();
  const [actionError, setActionError] = useState<Error | null>(null);
  const [busy, setBusy] = useState(false);
  const dt = DOCUMENT_TYPE_BY_CODE.get(doc.documentTypeCode);
  const version = doc.currentVersion;
  const canRead = doc.actions.includes('read');
  const preview = useDocumentPreview({ id: doc.id, status: doc.status, hidden: doc.hidden, canRead });
  const viewable = canRead && (doc.status === 'available' || doc.status === 'hidden' || doc.hidden);

  useEffect(() => {
    addRecentDocument({
      id: doc.id,
      title: doc.title,
      filename: version?.originalFilename ?? null,
      record: doc.entity ? `${KIND_LABEL[doc.entity.kind]} ${doc.entity.name}` : doc.folder.path,
      href: routes.document(doc.docNo),
    });
    notifyRecentChange();
  }, [doc.id, doc.docNo, doc.title, doc.entity, doc.folder.path, version?.originalFilename]);

  const download = async () => {
    setBusy(true);
    setActionError(null);
    try {
      const { blob, filename } = await api.documents.download(doc.id);
      saveBlob(blob, filename ?? version?.originalFilename ?? doc.title);
    } catch (err) {
      setActionError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  };

  const link = deepLink.document(String(doc.docNo), config.appBaseUrl);
  const crumbs = [
    ...(doc.entity ? [{ label: doc.entity.name, href: entityRoute(doc.entity.kind, doc.entity.number) }] : []),
    { label: doc.folder.name },
    { label: doc.title },
  ];

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <Breadcrumbs items={crumbs} />
          <div className="mt-2 flex items-center gap-3">
            <FilePlate filename={version?.originalFilename} mime={version?.detectedMime} size="lg" />
            <div className="min-w-0">
              <h1 className="truncate text-3xl leading-none">{doc.title}</h1>
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
                <span className="truncate" title={version?.originalFilename}>
                  {version?.originalFilename ?? 'No file yet'}
                </span>
                <span className="gsl-num" title="Document number">#{doc.docNo}</span>
                <Chip variant="neutral">{dt?.label ?? doc.documentTypeCode}</Chip>
                <StatusChip status={doc.status} hidden={doc.hidden} />
                {doc.legalHold ? <Chip variant="danger">Legal hold</Chip> : null}
                {dt?.sensitivity === 'restricted' ? <Chip variant="warning">Restricted</Chip> : null}
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button icon="download" disabled={!viewable || !doc.actions.includes('download')} busy={busy} onClick={() => void download()}>
            Download
          </Button>
          <CopyButton text={link} size="md" />
        </div>
      </header>

      {actionError ? <ErrorBanner error={actionError} /> : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_21rem]">
        <section className="gsl-panel overflow-hidden" aria-label="Document preview">
          <DocumentPreview preview={preview} title={doc.title} height="78vh" />
        </section>

        <aside className="space-y-4" aria-label="Document details">
          <div className="gsl-panel p-4 text-sm">
            <h2 className="text-lg leading-none">Details</h2>
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
              <dt className="text-muted-foreground">Size</dt>
              <dd className="gsl-num">{version ? formatBytes(version.sizeBytes) : '—'}</dd>
              <dt className="text-muted-foreground">Uploaded</dt>
              <dd>{version ? `${shortUpn(version.uploadedByUpn)}, ${formatDateTime(version.uploadedAt)}` : '—'}</dd>
              <dt className="text-muted-foreground">Folder</dt>
              <dd className="truncate" title={doc.folder.path}>
                {doc.folder.path}
              </dd>
              {doc.entity ? (
                <>
                  <dt className="text-muted-foreground">Record</dt>
                  <dd>
                    <Link className="gsl-link" to={entityRoute(doc.entity.kind, doc.entity.number)}>
                      {doc.entity.name} ({doc.entity.number})
                    </Link>
                  </dd>
                </>
              ) : null}
              {doc.retainUntil ? (
                <>
                  <dt className="text-muted-foreground">Retain until</dt>
                  <dd>{formatDate(doc.retainUntil)}</dd>
                </>
              ) : null}
              {version?.sha256 ? (
                <>
                  <dt className="text-muted-foreground">SHA-256</dt>
                  <dd className="gsl-num break-all">{version.sha256}</dd>
                </>
              ) : null}
            </dl>
          </div>

          {doc.versions && doc.versions.length > 0 ? (
            <div className="gsl-panel p-4 text-sm">
              <h2 className="text-lg leading-none">Versions</h2>
              <ol className="mt-3 divide-y divide-border">
                {[...doc.versions]
                  .sort((a, b) => b.versionNo - a.versionNo)
                  .map((v) => (
                    <li key={v.id} className="flex items-center gap-3 py-2 text-xs">
                      <span className={`gsl-num font-medium ${v.id === version?.id ? 'text-accent-foreground' : ''}`}>v{v.versionNo}</span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate" title={v.originalFilename}>
                          {v.originalFilename}
                        </span>
                        <span className="block text-muted-foreground">
                          {shortUpn(v.uploadedByUpn)}, {formatDateTime(v.uploadedAt)}
                        </span>
                      </span>
                      <span className="gsl-num text-muted-foreground">{formatBytes(v.sizeBytes)}</span>
                    </li>
                  ))}
              </ol>
            </div>
          ) : null}

          <RecordsActionsCard doc={doc} onChanged={onChanged} />
        </aside>
      </div>
    </div>
  );
}

function RecordsActionsCard({ doc, onChanged }: { doc: DocumentDetail; onChanged?: () => void }) {
  const any = ['hide', 'unhide', 'remove', 'hold'].some((a) => doc.actions.includes(a as DocumentDetail['actions'][number]));
  if (!any) return null;
  return (
    <div className="gsl-panel p-4 text-sm">
      <h2 className="text-lg leading-none">Records</h2>
      <p className="mb-3 mt-1 text-xs text-muted-foreground">Every action here is written to the audit trail.</p>
      <RecordsActions doc={doc} onChanged={onChanged} compact />
    </div>
  );
}
